import { getISOWeek, parseISO } from "date-fns";
import type { TimeGap } from "@/hooks/use-time-gaps";
import type { StatusTone } from "@/lib/widget-status";

/**
 * VIB-3034: felles status for en dag i Lukk måned, brukt av både kalenderen og
 * daglista, så de alltid farger samme dag likt.
 *
 * Reglene speiler `summarizeMissingDays`: helger, framtidige dager og dager
 * uten forventede timer er aldri hull.
 */
export type DayStatus = "ok" | "partial" | "missing" | "absence" | "holiday" | "future" | "weekend";

export function getDayStatus(gap: TimeGap): DayStatus {
  if (gap.isFuture) return "future";
  if (gap.isWeekend) return "weekend";
  // Helligdag uten føring vises nøytralt. Fører noen timer, er dagen ført.
  if (gap.isHoliday && gap.registeredHours <= 0) return "holiday";
  if (gap.isAbsent && gap.expectedHours <= 0) return "absence";
  if (gap.expectedHours <= 0) return "ok";
  if (gap.registeredHours <= 0) return "missing";
  if (gap.gap > 0) return "partial";
  return "ok";
}

/** Rad-id som kalenderen og «Gå til første tomme dag» ruller til. */
export function dayRowId(date: string): string {
  return `close-month-day-${date}`;
}

/** Statusfarge fra widget-status. Fravær, helligdag, helg og framtid er nøytrale. */
export const DAY_TONE: Partial<Record<DayStatus, StatusTone>> = {
  ok: "reached",
  partial: "near",
  missing: "miss",
};

/** Første dag å rette: første tomme dag, ellers første delvis førte. */
export function firstGapDate(gaps: TimeGap[]): string | null {
  const statuses = gaps.map((g) => ({ date: g.date, status: getDayStatus(g) }));
  return (
    statuses.find((s) => s.status === "missing")?.date ??
    statuses.find((s) => s.status === "partial")?.date ??
    null
  );
}

export interface WeekGroup<T> {
  week: number;
  items: T[];
}

/** Grupperer dager (sortert stigende) på ISO-uke, i rekkefølge. */
export function groupByIsoWeek<T extends { date: string }>(days: T[]): WeekGroup<T>[] {
  const groups: WeekGroup<T>[] = [];
  for (const day of days) {
    const week = getISOWeek(parseISO(day.date));
    const last = groups[groups.length - 1];
    if (last && last.week === week) last.items.push(day);
    else groups.push({ week, items: [day] });
  }
  return groups;
}
